/* ==========================================================================
   BOD – checkout polish
   - Buy-button "hook" (shake + light sweep) on the product page button,
     the sticky add to cart and the Releasit buttons (ORDER NOW, form submit).
   - Releasit COD form, same as Allure: French texts, 5 visible fields in this order
     (Nom et prénom, Ville, Adresse, Téléphone, WhatsApp), icons, autofill, city suggestions.
     Fields Releasit still requires but the customer does not see (last name, e-mail)
     are filled in automatically when the order is sent.
   - Order validation against fake / spam orders (Moroccan phone numbers).
   Settings come from Theme settings → "BOD – Conversion" (window.bodSettings).
   ========================================================================== */
(() => {
  if (window.BodCheckoutLoaded) return;
  window.BodCheckoutLoaded = true;

  const cfg = Object.assign(
    {
      ctaHook: true,
      hookInterval: 5,
      codForm: true,
      buttonColor: '', // empty = keep Releasit's own green
      emailDomain: 'client.booksondemand.ma',
      trustLine: true,
      trustItems: ['Paiement à la livraison', 'Livraison en 8–10 jours ouvrés', 'Commande sécurisée'],
    },
    window.bodSettings || {}
  );

  const MODAL = '#_rsi-cod-form-modal';
  const FORM = '#_rsi-cod-form-modal-form';

  const MESSAGES = {
    name: 'Entrez votre nom et prénom.',
    city: 'Indiquez votre ville.',
    address: 'Indiquez votre adresse (quartier, rue, numéro).',
    phone: 'Entrez un numéro de téléphone marocain valide, ex. 06 12 34 56 78.',
    phoneFake: 'Ce numéro semble incorrect. Vérifiez-le.',
    whatsapp: 'Entrez un numéro WhatsApp valide, ex. 06 12 34 56 78.',
  };

  // Releasit texts shown in French (exact English text → French).
  const TEXTS = {
    'Your Order': 'Votre commande',
    Subtotal: 'Sous-total',
    Shipping: 'Livraison',
    Total: 'Total',
    Discounts: 'Réductions',
    'Free Shipping': 'Livraison gratuite',
    Free: 'Gratuit',
    Apply: 'Appliquer',
    'Enter your contact and shipping details': 'Remplissez le formulaire suivant et obtenez votre commande maintenant!',
    'Check this box to confirm your order': 'Oui, veuillez confirmer et procéder à ma commande.',
    'Complete your purchase': 'Commander maintenant',
  };

  /* 1. Buy-button hook ------------------------------------------------------ */
  const HOOK_TARGETS = [
    '._rsi-buy-now-button',
    '._rsi-modal-submit-button',
    '[data-bod-hook]',
    '#MainContent product-info .product-form__submit[name="add"]',
  ].join(',');

  function applyHook(root) {
    if (!cfg.ctaHook) return;
    root.querySelectorAll(HOOK_TARGETS).forEach(button => {
      if (button.dataset.bodHooked) return;
      if (button.closest('.bod-up, quick-add-modal, .card-wrapper')) return;
      button.dataset.bodHooked = 'true';
      button.style.setProperty('--bod-hook-interval', `${cfg.hookInterval}s`);
      button.classList.add('bod-hook');
      button.addEventListener('pointerdown', () => {
        button.classList.add('is-paused');
        clearTimeout(button.bodResume);
        button.bodResume = setTimeout(() => button.classList.remove('is-paused'), 4000);
      });
    });
  }

  /* 2. Checks ----------------------------------------------------------------- */
  // Returns the Moroccan number as 0XXXXXXXXX, or '' when it cannot be one.
  function normalizeMoroccan(raw) {
    let digits = String(raw || '').replace(/[^\d+]/g, '');
    if (digits.startsWith('+212')) digits = '0' + digits.slice(4);
    else if (digits.startsWith('00212')) digits = '0' + digits.slice(5);
    else if (digits.startsWith('212') && digits.length === 12) digits = '0' + digits.slice(3);
    else if (/^[5-7]\d{8}$/.test(digits)) digits = '0' + digits;
    if (digits.startsWith('00')) return '';
    return /^0\d{9}$/.test(digits) ? digits : '';
  }

  // Patterns typed by people who do not want to give a real number.
  function looksFake(number) {
    const body = number.slice(-8);
    if (new Set(body).size < 3) return true; // 0666666666, 0612121212
    if (/(\d)\1{5,}/.test(number)) return true; // six identical digits in a row
    if ('01234567890123456789'.includes(body) || '98765432109876543210'.includes(body)) return true; // 0612345678
    return false;
  }

  // Releasit may show a +212 prefix next to the field and expect the number without it.
  function phoneOutput(input, local) {
    const prefixed = input.closest('._rsi-modal-fields-item')?.textContent.includes('+212');
    return prefixed ? local.slice(1) : local;
  }

  function checkPhone(value, { whatsapp = false } = {}) {
    const raw = String(value || '').trim();
    const message = whatsapp ? MESSAGES.whatsapp : MESSAGES.phone;
    if (!raw) return { ok: false, message };
    const local = normalizeMoroccan(raw);
    if (local) {
      if (!/^0[5-7]/.test(local)) return { ok: false, message };
      if (whatsapp && !/^0[67]/.test(local)) return { ok: false, message };
      if (looksFake(local)) return { ok: false, message: MESSAGES.phoneFake };
      return { ok: true, local };
    }
    // WhatsApp may be a foreign number (family abroad): +CC followed by 7–14 digits.
    if (whatsapp) {
      const intl = raw.replace(/[^\d+]/g, '').replace(/^00/, '+');
      if (/^\+[1-9]\d{7,14}$/.test(intl) && !intl.startsWith('+212')) {
        if (looksFake(intl)) return { ok: false, message: MESSAGES.phoneFake };
        return { ok: true, value: intl };
      }
    }
    return { ok: false, message };
  }

  const hasLink = text => /(https?:|www\.|\.com\b|\.ma\b|<|>)/i.test(text);
  const clean = value => String(value || '').trim().replace(/\s+/g, ' ');

  function checkName(value) {
    const text = clean(value);
    const letters = text.replace(/[^\p{L}]/gu, '');
    if (letters.length < 3 || /\d/.test(text) || hasLink(text) || /(\p{L})\1{3,}/u.test(text)) return { ok: false, message: MESSAGES.name };
    return { ok: true, value: text };
  }

  function checkCity(value) {
    const text = clean(value);
    const letters = text.replace(/[^\p{L}]/gu, '');
    if (letters.length < 2 || /\d{3,}/.test(text) || hasLink(text)) return { ok: false, message: MESSAGES.city };
    return { ok: true, value: text };
  }

  function checkAddress(value) {
    const text = clean(value);
    const letters = text.replace(/[^\p{L}]/gu, '');
    if (text.length < 5 || letters.length < 3 || hasLink(text) || /(.)\1{5,}/u.test(text)) return { ok: false, message: MESSAGES.address };
    return { ok: true, value: text };
  }

  /* 3. Fields ------------------------------------------------------------------
     order: position in the form (Allure layout). hidden: kept for Releasit, filled automatically. */
  const FIELDS = [
    { selector: 'input[name="first_name"]', kind: 'name', order: 1, placeholder: 'Nom et prénom', autocomplete: 'name', autocapitalize: 'words', check: checkName },
    { selector: 'input[name="city"]', kind: 'city', order: 2, placeholder: 'Ville', autocomplete: 'address-level2', autocapitalize: 'words', check: checkCity },
    { selector: 'input[name="address"], textarea[name="address"]', kind: 'address', order: 3, placeholder: 'Adresse (quartier, rue, n°)', autocomplete: 'street-address', check: checkAddress },
    { selector: 'input[name="phone"]', kind: 'phone', order: 4, placeholder: 'Numéro de téléphone', autocomplete: 'tel', phone: true, check: v => checkPhone(v) },
    {
      // Releasit field "Confirm Phone Number" (civic_number) is used as the WhatsApp number.
      selector: 'input[name="civic_number"], input[name^="additionals_text_input_Confirm"], input[name*="whatsapp" i]',
      kind: 'whatsapp',
      order: 5,
      placeholder: 'Numéro WhatsApp',
      autocomplete: 'tel',
      phone: true,
      check: v => checkPhone(v, { whatsapp: true }),
    },
    { selector: 'input[name="last_name"]', kind: 'last-name', hidden: true },
    { selector: 'input[name="email"]', kind: 'email', hidden: true },
    { selector: 'input[name="note"], textarea[name="note"]', kind: 'note', hidden: true },
  ];

  function fieldError(input, message) {
    const item = input.closest('._rsi-modal-fields-item');
    if (!item) return;
    let error = item.querySelector('.bod-error');
    if (!error) {
      error = document.createElement('p');
      error.className = 'bod-error';
      error.id = `bod-error-${Math.random().toString(36).slice(2, 8)}`;
      error.setAttribute('role', 'alert');
      item.appendChild(error);
    }
    item.classList.toggle('is-invalid', !!message);
    item.classList.toggle('is-valid', !message && !!input.value.trim());
    error.hidden = !message;
    error.textContent = message || '';
    if (message) input.setAttribute('aria-describedby', error.id);
    input.setAttribute('aria-invalid', String(!!message));
  }

  // Applies a check: fixes the value when it can (spaces, +212...), shows the message otherwise.
  function runCheck(input, field) {
    const result = field.check(input.value);
    let next = result.value;
    if (result.ok && result.local) next = phoneOutput(input, result.local);
    if (result.ok && next && next !== input.value) setValue(input, next);
    fieldError(input, result.ok ? '' : result.message);
    return result.ok;
  }

  function setValue(input, value) {
    input.value = value;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Replaces Releasit's English texts (also after Releasit re-renders parts of the form).
  function translate(modal) {
    const walker = document.createTreeWalker(modal, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      const text = node.nodeValue.trim();
      if (text && Object.prototype.hasOwnProperty.call(TEXTS, text)) node.nodeValue = node.nodeValue.replace(text, TEXTS[text]);
    }
    const discount = modal.querySelector('#_rsi-modal-discounts-form-code');
    if (discount && discount.placeholder !== 'Code promo') {
      discount.placeholder = 'Code promo';
      discount.setAttribute('aria-label', 'Code promo');
    }
  }

  function enhanceForm(modal) {
    const form = modal.querySelector('form');
    if (!form) return;

    if (!modal.classList.contains('bod-cod')) {
      modal.classList.add('bod-cod');
      modal.dataset.bodOpenedAt = String(Date.now());
    }
    form.setAttribute('autocomplete', 'on');
    form.querySelector('._rsi-modal-fields')?.classList.add('bod-fields');
    translate(modal);

    FIELDS.forEach(field => {
      const input = form.querySelector(field.selector);
      if (!input || input.dataset.bodField) return;
      input.dataset.bodField = field.kind;
      const item = input.closest('._rsi-modal-fields-item');

      if (field.hidden) {
        if (item) item.classList.add('bod-field--hidden');
        input.setAttribute('tabindex', '-1');
        input.setAttribute('autocomplete', 'off');
        return;
      }

      if (item) {
        item.classList.add('bod-field', `bod-field--${field.kind}`);
        item.style.order = field.order;
      }
      input.classList.add('bod-field__input');
      input.placeholder = field.placeholder;
      input.setAttribute('aria-label', field.placeholder);
      input.setAttribute('autocomplete', field.autocomplete);
      if (field.autocapitalize) input.setAttribute('autocapitalize', field.autocapitalize);
      input.setAttribute('spellcheck', 'false');

      if (field.phone) {
        input.type = 'tel';
        input.setAttribute('inputmode', 'tel');
        input.setAttribute('maxlength', '17');
        // Digits, spaces and a leading + only.
        input.addEventListener('input', () => {
          const cleaned = input.value.replace(/[^\d+\s]/g, '').replace(/(?!^)\+/g, '');
          if (cleaned !== input.value) input.value = cleaned;
          if (input.closest('.is-invalid')) {
            const result = field.check(input.value);
            fieldError(input, result.ok ? '' : result.message);
          }
        });
      }

      const validate = () => {
        if (input.value.trim()) runCheck(input, field);
      };
      input.addEventListener('blur', validate);
      input.addEventListener('change', validate); // browser autofill
      if (!field.phone) {
        input.addEventListener('input', () => {
          if (input.closest('.is-invalid') && field.check(input.value).ok) fieldError(input, '');
        });
      }

      if (field.kind === 'city') enhanceCity(input);
    });

    // Confirmation checkbox and order button after the fields.
    form.querySelector('._rsi-modal-fields-checkbox')?.style.setProperty('order', '20');

    alignSubmit(form);
    addTrustLine(form);
  }

  // Fills what Releasit requires but the customer does not see.
  function fillHidden(form) {
    const name = form.querySelector('input[name="first_name"]');
    const last = form.querySelector('input[name="last_name"]');
    if (name && last && last.closest('.bod-field--hidden')) {
      // Already split by an earlier tap (order not sent): rebuild the full name first.
      const full = name.dataset.bodSplit === name.value && last.value ? `${name.value} ${last.value}` : name.value;
      const parts = clean(full).split(' ');
      name.dataset.bodSplit = parts[0] || '';
      if (parts.length > 1) {
        setValue(name, parts.shift());
        setValue(last, parts.join(' '));
      } else {
        setValue(last, parts[0] || '');
      }
    }
    const email = form.querySelector('input[name="email"]');
    if (email && email.closest('.bod-field--hidden') && !/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(email.value)) {
      const phone = normalizeMoroccan(form.querySelector('input[name="phone"]')?.value) || Date.now().toString();
      setValue(email, `${phone}@${cfg.emailDomain}`);
    }
  }

  // Releasit writes its button styles inline with !important, so they are set here.
  function alignSubmit(form) {
    const submit = form.querySelector('._rsi-modal-submit-button');
    if (!submit || submit.dataset.bodSubmit) return;
    submit.dataset.bodSubmit = 'true';
    if (cfg.buttonColor) {
      submit.style.setProperty('background', cfg.buttonColor, 'important');
      submit.style.setProperty('border-color', cfg.buttonColor, 'important');
      submit.style.setProperty('color', '#fff', 'important');
    }
    submit.style.setProperty('border-radius', '14px', 'important');
    submit.style.setProperty('box-shadow', '0 10px 24px -12px rgba(18, 18, 18, 0.45)', 'important');
  }

  const TRUST_ICONS = [
    '<path d="M3 7.5h18v10H3z"/><circle cx="12" cy="12.5" r="2.4"/><path d="M6.5 10v5M17.5 10v5"/>',
    '<path d="M2.5 6.5h11v9h-11z"/><path d="M13.5 9.5h4l3 3.2v2.8h-7"/><circle cx="6.5" cy="17.5" r="1.8"/><circle cx="17" cy="17.5" r="1.8"/>',
    '<path d="M12 3 4.5 6v5.5c0 4.6 3.2 8.4 7.5 9.5 4.3-1.1 7.5-4.9 7.5-9.5V6L12 3Z"/><path d="m8.8 12.2 2.2 2.2 4.3-4.4"/>',
  ];

  function addTrustLine(form) {
    const items = (cfg.trustItems || []).filter(Boolean);
    if (!cfg.trustLine || !items.length) return;
    const submit = form.querySelector('._rsi-modal-submit-button');
    if (!submit || form.querySelector('.bod-cod-trust')) return;
    const list = document.createElement('ul');
    list.className = 'bod-cod-trust';
    list.setAttribute('role', 'list');
    list.innerHTML = items
      .map(
        (text, i) =>
          `<li><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${TRUST_ICONS[i % TRUST_ICONS.length]}</svg>${escapeHtml(text)}</li>`
      )
      .join('');
    submit.insertAdjacentElement('afterend', list);
  }

  /* 4. Submit guard (runs before Releasit) ------------------------------------- */
  function validateAll(form) {
    const problems = [];
    FIELDS.forEach(field => {
      if (field.hidden || !field.check) return;
      const input = form.querySelector(field.selector);
      if (!input || input.offsetParent === null) return;
      if (!runCheck(input, field)) problems.push(input);
    });
    return problems;
  }

  function guard(event) {
    const form = event.target.closest && event.target.closest(FORM);
    if (!form || !cfg.codForm) return;
    if (event.type === 'click' && !event.target.closest('._rsi-modal-submit-button')) return;

    const modal = form.closest(MODAL);
    const problems = validateAll(form);
    const openedAt = Number(modal && modal.dataset.bodOpenedAt) || 0;
    const tooFast = openedAt && Date.now() - openedAt < 2500; // bots fill everything instantly

    if (problems.length || tooFast) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const first = problems[0];
      if (first) {
        first.focus({ preventScroll: true });
        first.scrollIntoView({ block: 'center', behavior: 'smooth' });
        const item = first.closest('._rsi-modal-fields-item');
        if (item) {
          item.classList.remove('bod-shake');
          void item.offsetWidth;
          item.classList.add('bod-shake');
        }
      }
      return;
    }

    fillHidden(form);

    // One order per tap: ignore extra taps while Releasit sends the order.
    // Only clicks are locked; the form's own submit event that follows a click must go through.
    if (event.type !== 'click') return;
    const now = Date.now();
    const lastTap = Number(form.dataset.bodLastTap) || 0;
    if (now - lastTap < 3000) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    form.dataset.bodLastTap = String(now);
  }
  window.addEventListener('click', guard, true);
  window.addEventListener('submit', guard, true);

  /* 5. City suggestions (free typing stays allowed) --------------------------- */
  const normalize = text =>
    String(text || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const escapeHtml = text => String(text).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  // Same city list as the Allure order form (Releasit "Ville" dropdown).
  const CITY_NAMES = [
    "Adouz", "Afourer", "Agadir", "Agdz", "Aghmat", "Aglmous", "Aglou", "Agni Izimmer", "Agourai", "Ahfir",
    "Ain Aouda", "Ain Atig", "Ain Beni Mathar", "Ain Dorij", "Ain Hamra", "Ain Harrouda", "Ain Johra", "Ain Karma",
    "Ain Taoujdate", "Ait Addou", "Ait Ali", "Ait Amira", "Ait Baha", "Ait Ben Haddou", "Ait Hanik", "Ait Ishak",
    "Ait Kamra", "Ait Karmas", "Ait Laaza", "Ait Marghade", "Ait Meloul", "Ait Ourir", "Ait Rbaa", "Ait Zineb",
    "Ajdir", "Ajdir (region taza)", "Akhfennir", "Aklim", "Aknoul", "Al Aaroui", "Al Hoceima", "Al Rahma",
    "Alnif", "Amzarou", "Amzmiz", "Anwal", "Anza", "Aoulouz", "Arbaa Sahel", "Arfoud", "Arkman", "Assa", "Assahrij",
    "Assilah", "Awrir", "Azemmour", "Azilal", "Azla", "Azrou", "Bab Attoutchy", "Bab Berred", "Bab Taza", "Battou",
    "Bejaad", "Belaaguid", "Belfaa", "Ben Ahmed", "Ben Guerir", "Beni Ansar", "Beni Ayat", "Beni Hilal", "Beni KLA",
    "Beni Mellal", "Benslimane", "Bentayab", "Bergem", "Berkane", "Berrchid", "Bhalil", "Biougra", "Bir Jdid",
    "Bir Mezoui", "Bni Bouayach", "Bni Drar", "Bni Oukil", "Bouanane", "Bouarfa", "Bouarg", "Bouderbala", "Boudinar",
    "Boudnib", "Boufakrane", "Bouhlou", "Bouhmama", "Bouizakarne", "Boujdour", "Boujniba", "Boukidan", "Boulanouar",
    "Boulemane", "Boumalne Dades", "Boumia", "Bourd", "Bousfer", "Bouskoura", "Bouznika", "Briech", "Cabo Negro",
    "Casablanca", "Caouna", "Chefchaouen", "Chemaia", "Chichaoua", "Chouiter", "Dakhla", "Dar Bouaza", "Dar Chaoui",
    "Dar El Kebdani", "Dar Oulad Zidouh", "Dar Si Aissa", "Debdou", "Demnate", "Deroua", "Dcheira", "Dkhissa",
    "Douar Bouazza", "Douar Sultan", "Drarga", "Driouch", "El Bazzaza", "El Beddouza", "El Cap", "El Gara",
    "El Hajeb", "El Hajeb Kaddour", "El Haouzia", "El Jadida", "El Kebab", "El Kelaa Des Sraghna", "El Khelfia",
    "El Ksiba", "El Maader El Kabir", "El Menzel", "Errachidia", "Errich", "Essaouira", "Essmara", "Farkhana",
    "Fes", "Figuig", "Fnideq", "Foum El Ansar", "Foum Oudi", "Foum Zguid", "Fquih Ben Salah", "Ghazoua", "Goulmima",
    "Guercif", "Guelmim", "Had Boumoussa", "Had Bradia", "Had Draa", "Had Hrara", "Had Laaounate", "Had Ouled Zbir",
    "Had Soualem", "Harhoura", "Hattane", "Hicham Azghar", "Hjar Nhal", "Houara", "Houara Agadir", "Ida Ousmlal",
    "Ifran", "Ighrem Laalam", "Ihaddaden", "Illigha", "Imintanoute", "Imouzzer Kandar", "Imzouren", "Inzegane",
    "Issaguen", "Jaadar", "Jebha", "Jemaa El Houdrane", "Jemaat Metouia", "Jemaat Shaim", "Jerada", "Jorf Lasfar",
    "Jouamaa", "Kalaat Mgouna", "Kaldmane", "Karia Ba Mohammed", "Kasbah Tadla", "Kef En Nsour", "Kenitra",
    "Kerrouchen", "Khemisset", "Khenichet", "Khenifra", "Khmiss Zemamra", "Khouribga", "Ksar El Kebir", "Ksar El Majaz",
    "Ksar Sghir", "Laakarta", "Laayoun Cherqia", "Laayoune", "Laayayta Ouled Gnaou", "Laaounate", "Lagouassem",
    "Lakhyayta", "Lamris", "Lalla Mimouna", "Laouamra", "Larache", "Laqliaa", "Laattaouia", "Lqliaa", "M Diq",
    "Madagh", "Mansouria", "Marina", "Marrakech", "Mariouari", "Martil", "Massa", "Mazagan Beach", "Mechra Bel Ksiri",
    "Mediouna", "Mejji", "Meknes", "Merzouga", "Mers El Kheir", "Midar", "Midelt", "Mirleft", "Missour", "Mohammedia",
    "Moulay Abdellah", "Moulay Bousselham", "Moulay Brahim", "Moulay Idriss Zerhoun", "Moulay Yacoub", "Mrirt",
    "Msawar Rasso", "Mzoudia", "Nador", "Naima", "Nkob", "Nouaceur", "Oualidia", "Ouad Laou", "Ouahat Sidi Brahim",
    "Ouaouizeght", "Ouaoumana", "Ouarzazate", "Oued Amlil", "Oued Jdida", "Oued Zem", "Oujda", "Oulad Abbou",
    "Oulad Ali", "Oulad Amrane", "Oulad Ayad", "Oulad Berhil", "Oulad Driss", "Oulad Ettayeb", "Oulad Fraj",
    "Oulad Ghanem", "Oulad Gouaouch", "Oulad Hassoune", "Oulad Mbarek", "Oulad Moussa", "Oulad Said El Oued",
    "Oulad Settout", "Oulad Si Bouhya", "Oulad Smail", "Oulad Tayeb", "Oulad Teima", "Oulad Yahya", "Oulad Yaiche",
    "Oulad Youssef", "Ounagha", "Ourika", "Outat El Haj", "Oudaya", "Ouazzane", "Rabat", "Ras El Ma", "Regada",
    "Riouni", "Rissani", "Sabaa Aiyoun", "Safi", "Saidia", "Sale", "Sale El Jadida", "Sebt El Guerdane", "Sebt El Maarif",
    "Sebt Gzoula", "Sebt Oulad Nemma", "Sefrou", "Selouane", "Settat", "Sidi Abed", "Sidi Abdallah Ghiat",
    "Sidi Abdellah Ghiat", "Sidi Ali", "Sidi Ali Bourakba", "Sidi Allal El Bahraoui", "Sidi Allal Tazi", "Sidi Bennour",
    "Sidi Bernoussi", "Sidi Bibi", "Sidi Bou Othmane", "Sidi Bouknadel", "Sidi Bouzid", "Sidi Hajjaj", "Sidi Ifni",
    "Sidi Jaber", "Sidi Kacem", "Sidi Lyamani", "Sidi Mokhtar", "Sidi Moussa", "Sidi Moussa Ben Ali", "Sidi Rahal",
    "Sidi Rehal", "Sidi Smail", "Sidi Sliman", "Sidi Taibi", "Sidi Yahya El Gharb", "Sidi Yahya Zaer", "Sidi Zouine",
    "Skhirat", "Skhour Rhamna", "Skoura", "Souani", "Souihla", "Souiria", "Souk El Arbaa Du Gharb", "Stehat",
    "Taadadate", "Taddart", "Tafraoute", "Taghazoute", "Tagomite", "Tagzirt", "Tahannaout", "Tahla", "Taliouine",
    "Tala Youssef", "Tamelalt", "Tamegroute", "Tamaris", "Tamansourt", "Tamraght", "Tanger", "Tanougha", "Tanout",
    "Tan Tan", "Tantan Plage", "Taouima", "Taounate", "Taourirt", "Targuist", "Taroudannt", "Tarmigte", "Tata",
    "Taza", "Tazzarine", "Tendrara", "Temara", "Tetouan", "Tezotene", "Tiflet", "Tifoultoute", "Tighsalin",
    "Tidrin", "Tikiouine", "Tilmia", "Timdline", "Timoulilt", "Tinjdad", "Tinghir", "Tiznit", "Tizi Ousli",
    "Tlata Taghramt", "Tnine Chtouka", "Tnine El Ghiate", "Tnine Mhaya", "Touilate", "Toumliouine", "Youssoufia",
    "Zagora", "Zaio", "Zaouiat Cheikh", "Zeghanghane", "Zoumi",
  ];
  // Main cities first ("Mar" → Marrakech before Marina, and at the top of the full list).
  const MAIN_CITIES = [
    'casablanca', 'rabat', 'marrakech', 'fes', 'tanger', 'agadir', 'meknes', 'oujda', 'kenitra', 'tetouan',
    'sale', 'temara', 'mohammedia', 'el jadida', 'beni mellal', 'nador', 'safi', 'khouribga', 'settat', 'larache',
  ];
  const cityRank = key => {
    const index = MAIN_CITIES.indexOf(key);
    return index === -1 ? MAIN_CITIES.length : index;
  };
  const CITIES = CITY_NAMES.map(label => ({ label, key: normalize(label) }))
    .map(city => ({ ...city, rank: cityRank(city.key) }))
    .sort((a, b) => a.rank - b.rank || a.label.localeCompare(b.label, 'fr'));

  function enhanceCity(input) {
    if (input.dataset.bodCity) return;
    input.dataset.bodCity = 'true';

    const uid = `bod-city-${Math.random().toString(36).slice(2, 8)}`;
    const wrap = document.createElement('div');
    wrap.className = 'bod-city';
    input.parentNode.insertBefore(wrap, input);
    wrap.appendChild(input);
    const list = document.createElement('ul');
    list.className = 'bod-city__list';
    list.id = uid;
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-label', 'Villes');
    wrap.appendChild(list);

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', uid);

    let matches = [];
    let active = -1;

    const open = isOpen => {
      wrap.classList.toggle('is-open', isOpen);
      input.setAttribute('aria-expanded', String(isOpen));
    };

    function search(query) {
      const q = normalize(query);
      if (!q) return CITIES; // tap on the empty field: the whole list, main cities first
      const starts = [];
      const words = [];
      const contains = [];
      CITIES.forEach(city => {
        if (city.key.startsWith(q)) starts.push(city);
        else if (city.key.split(' ').some(word => word.startsWith(q))) words.push(city);
        else if (city.key.includes(q)) contains.push(city);
      });
      return starts.concat(words, contains).slice(0, 30);
    }

    function highlight(label, query) {
      const q = normalize(query);
      const key = normalize(label);
      const index = q ? key.indexOf(q) : -1;
      if (index === -1 || key.length !== label.length) return escapeHtml(label);
      return `${escapeHtml(label.slice(0, index))}<mark>${escapeHtml(label.slice(index, index + q.length))}</mark>${escapeHtml(label.slice(index + q.length))}`;
    }

    function render() {
      matches = search(input.value);
      const exact = matches.length === 1 && matches[0].key === normalize(input.value);
      if (!matches.length || exact) {
        open(false);
        return;
      }
      active = -1;
      list.innerHTML = matches
        .map((city, i) => `<li class="bod-city__option" role="option" id="${uid}-${i}" data-index="${i}" aria-selected="false">${highlight(city.label, input.value)}</li>`)
        .join('');
      open(true);
    }

    function moveActive(step) {
      if (!matches.length) return;
      active = (active + step + matches.length) % matches.length;
      list.querySelectorAll('.bod-city__option').forEach((li, i) => {
        li.classList.toggle('is-active', i === active);
        if (i === active) li.scrollIntoView({ block: 'nearest' });
      });
      input.setAttribute('aria-activedescendant', `${uid}-${active}`);
    }

    function choose(city) {
      setValue(input, city.label);
      fieldError(input, '');
      open(false);
      const next = input.form && input.form.querySelector('input[name="address"], textarea[name="address"]');
      if (next) next.focus({ preventScroll: false });
    }

    input.addEventListener('input', event => {
      if (event.isTrusted) render();
    });
    input.addEventListener('focus', () => {
      render();
      // Bring the field to the top of the form so the list is not hidden behind the order button or keyboard.
      setTimeout(() => wrap.scrollIntoView({ block: 'start', behavior: 'smooth' }), 60);
    });
    input.addEventListener('keydown', event => {
      if (!wrap.classList.contains('is-open')) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        moveActive(1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        moveActive(-1);
      } else if (event.key === 'Enter' && matches[active]) {
        event.preventDefault();
        choose(matches[active]);
      } else if (event.key === 'Escape' || event.key === 'Tab') {
        open(false);
      }
    });
    input.addEventListener('blur', () => setTimeout(() => open(false), 160));

    // Tap on a suggestion: keep focus in the field until the choice is made.
    list.addEventListener('pointerdown', event => event.preventDefault());
    list.addEventListener('click', event => {
      const li = event.target.closest('.bod-city__option');
      if (li) choose(matches[Number(li.dataset.index)]);
    });
  }

  /* Boot --------------------------------------------------------------------- */
  let scheduled = false;
  function run() {
    scheduled = false;
    applyHook(document);
    if (!cfg.codForm) return;
    const modal = document.querySelector(MODAL);
    if (!modal) return;
    if (modal.classList.contains('_rsi-cod-form-modal-open')) enhanceForm(modal);
    else if (modal.classList.contains('bod-cod')) {
      // Closed: the next opening starts a fresh timer for the anti-bot check.
      modal.dataset.bodOpenedAt = '';
      modal.classList.remove('bod-cod');
    }
  }
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(run);
  }

  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();
